import {useRedirector} from '@tnc/shared';
import {useEffect, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router';

import {SupabaseInstance} from '@/lib/utils/supabaseClass';

import {useAuth} from '@/providers/components/authProvider';

import {LoadingSpinningLogoComponent} from '@/components';

export const AuthCallbackPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {isAuthenticated, authenticating, actingAs, userAccounts} = useAuth();

  const {trackReferralSignup} = useRedirector();

  // State
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Explicitly exchange the auth code for a session
        // This is a critical step for OAuth providers
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        if (hashParams.get('access_token')) {
          console.log('Processing OAuth redirect with hash parameters');
          const {data, error: sessionError} = await SupabaseInstance.supabase.auth.getSession();
          if (sessionError) throw sessionError;
          console.log('Session after OAuth redirect:', data);

          // Add a short delay to allow auth state to update
          await new Promise(resolve => setTimeout(resolve, 1000));
        }

        // Ensure the users has a valid session
        const {error} = await SupabaseInstance.getCurrentSession();
        if (error) throw error;

        // If the user is not a new user, redirect to the appropriate dashboard
        if (userAccounts?.verification?.is_new_user) {
          // Get account_type from React Router's searchParams
          const accountType = searchParams.get('account_type') || 'speculator';
          navigate(`/register-details`, {replace: true, state: {accountType}});
          return;
        }

        // Get error description if present
        const errorDescription = searchParams.get('error_description');
        if (errorDescription) throw new Error(errorDescription);

        if (isAuthenticated) {
          console.log('Tracking referral signup');
          await trackReferralSignup();

          const _actingAs = actingAs ?? 'speculator';
          navigate(`/${_actingAs}/dashboard`, {replace: true});
        } else {
          console.log('Not authenticated, navigating to sign-in');
          navigate('/sign-in', {replace: true});
        }
      } catch (error) {
        console.error('Error handling auth callback:', error);
        setError(error instanceof Error ? error.message : 'Authentication failed');
        navigate('/sign-in', {replace: true});
      }
    };

    if (!authenticating) {
      handleCallback();
    }
  }, [navigate, isAuthenticated, authenticating, searchParams, actingAs, userAccounts, trackReferralSignup]);

  if (error) {
    return <div>Authentication failed: {error}</div>;
  }

  return <LoadingSpinningLogoComponent />;
};
